import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { IntlProvider } from 'use-intl';
import { isSupportedLocale, resolveLocale, type Locale, type ResolveLocaleInput } from '@financialharmony/i18n';
import englishMessages from '@financialharmony/i18n/en';
import { raiseLocaleAlerts } from '@financialharmony/i18n/alerts';
import { loadMessages, type Messages } from '@financialharmony/i18n/messages';
import { loadPseudoMessages, PSEUDO_LOCALE, pseudoLocaleAllowed } from '@financialharmony/i18n/pseudo';
import { useAuth } from '@/context/AuthContext';
import { userPreferencesApiClient } from '@/clients/userPreferencesApiClient';
import { Sentry } from '@/lib/sentry';
import { isLocaleStorageEvent, readStoredLocale, writeStoredLocale } from './localeStorage';
import { LocaleContext } from './useUiLocale';
import { setActiveCatalog } from './translateOutsideReact';
import { i18nAlerts, i18nHandlers } from './i18nAlerts';

function browserLanguages(): string {
  return typeof navigator === 'undefined' ? '' : (navigator.languages ?? [navigator.language]).join(',');
}

function resolveAndReport(input: ResolveLocaleInput) {
  const resolved = resolveLocale(input);
  raiseLocaleAlerts(i18nAlerts, input, resolved);
  return resolved;
}

function initialLocale(): Locale {
  return resolveAndReport({ cookie: readStoredLocale(), acceptLanguage: browserLanguages() }).locale;
}

// Developer switch: `?locale=en-XA` shows English with every sentence accented and padded, so text that bypasses
// the catalog stays plain. Never offered in a production build, and never stored as the person's language.
function pseudoLocaleRequested(): boolean {
  return (
    pseudoLocaleAllowed(import.meta.env.MODE) &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('locale') === PSEUDO_LOCALE
  );
}

/**
 * The UI language of the consultant app. Order: this browser's saved choice → the account's language
 * (read after sign-in) → the browser's languages → English. Switching re-renders in place: the catalog
 * for the new language loads first and the screen keeps showing the old one until it is ready.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [pseudo] = useState(pseudoLocaleRequested);
  const [locale, setLocaleState] = useState<Locale>(() => (pseudo ? 'en' : initialLocale()));
  const [loaded, setLoaded] = useState<{ locale: Locale; messages: Messages } | null>(
    locale === 'en' && !pseudo ? { locale: 'en', messages: englishMessages } : null,
  );
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (pseudo ? loadPseudoMessages() : loadMessages(locale))
      .then((messages) => {
        if (!cancelled) setLoaded({ locale, messages });
      })
      .catch((error: unknown) => {
        // A catalog that cannot load must not leave the app blank: stay on what is shown, or English.
        Sentry.captureException(error);
        if (!cancelled) setLoaded((current) => current ?? { locale: 'en', messages: englishMessages });
      });
    return () => {
      cancelled = true;
    };
  }, [locale, pseudo]);

  useEffect(() => {
    if (loaded) setActiveCatalog(loaded.locale, loaded.messages);
  }, [loaded]);

  useEffect(() => {
    document.documentElement.lang = loaded?.locale ?? locale;
  }, [loaded, locale]);

  // Another tab chose a language: follow it.
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (isLocaleStorageEvent(event) && isSupportedLocale(event.newValue)) setLocaleState(event.newValue);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Sign-in: the saved account language wins over this browser's; an account with none takes the
  // language this browser already shows (a choice made on the sign-in page, or the browser's own).
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const stored = await userPreferencesApiClient.getLocale();
        const resolved = resolveAndReport({ profile: stored, cookie: readStoredLocale(), acceptLanguage: browserLanguages() });
        if (resolved.source === 'cookie' && !isSupportedLocale(stored)) {
          await userPreferencesApiClient.setLocale(resolved.locale);
        }
        if (cancelled) return;
        writeStoredLocale(resolved.locale);
        setLocaleState(resolved.locale);
      } catch (error) {
        // The language is a convenience: keep what is shown, and say so for the next attempt.
        Sentry.captureException(error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const setLocale = useCallback(async (next: Locale): Promise<boolean> => {
    if (userRef.current) {
      try {
        await userPreferencesApiClient.setLocale(next);
      } catch (error) {
        Sentry.captureException(error);
        return false;
      }
    }
    writeStoredLocale(next);
    setLocaleState(next);
    return true;
  }, []);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  if (!loaded) return null;
  return (
    <LocaleContext.Provider value={value}>
      <IntlProvider
        locale={loaded.locale}
        messages={loaded.messages}
        timeZone="Europe/Stockholm"
        onError={i18nHandlers.onError}
        getMessageFallback={i18nHandlers.getMessageFallback}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}
